/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {Controller} from 'react-hook-form'
import {
    FormControl,
    IconButton,
    InputGroup,
    InputRightElement,
    useMultiStyleConfig,
    Text,
    Select,
} from '@chakra-ui/react'
import {VisibilityIcon, VisibilityOffIcon} from '@salesforce/retail-react-app/app/components/icons'
import {useIntl} from 'react-intl'
import InputComponent from './input-component'
import PhoneComponent from './phone-component'
import TextAreaComponent from './text-area-component'
import Dropdown from '../dropdown'
import LoqateComponent, {getCustomInputValue} from '../loqate-component'
import DatePicker from '../date-picker'
import Checkbox from '../../components/checkbox/index'
import {validatePhoneNumber} from '../../utils/form-utils'

const Field = ({
    name,
    label,
    formLabel,
    type = 'text',
    options = [],
    rules = {},
    error,
    placeholder,
    inputProps,
    isDisabled,
    isSearchable,
    control,
    autoComplete,
    defaultValue,
    helpText,
    children,
    variant,
    isProductRegistration,
    inputRef,
    toggleAutoComplete,
    setAutoCompleteAvailable,
    shouldRemoveLabel = false,
    calendarIcon,
    clearIcon,
    borderColor,
    tooltip,
    tooltipVariant,
    tooltipOpensOn,
    shouldDisplaySuccess = true,
    hideSuccessIcon = false,
    isEditForm,
    maxDate,
    isContactUs = false,
    ...rest
}) => {
    const {formatMessage} = useIntl()

    const [hidePassword, setHidePassword] = useState(true)
    const PasswordIcon = hidePassword ? VisibilityIcon : VisibilityOffIcon
    const passwordIconLabel = hidePassword
        ? formatMessage({
              id: 'field.password.assistive_msg.show_password',
              defaultMessage: 'Show password',
          })
        : formatMessage({
              id: 'field.password.assistive_msg.hide_password',
              defaultMessage: 'Hide password',
          })
    const inputType =
        type === 'password' && hidePassword ? 'password' : type === 'password' ? 'text' : type

    const styles = useMultiStyleConfig('Field')

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            defaultValue={defaultValue}
            render={({field: {onChange, onBlur, value, ref}}) => {
                const _inputProps =
                    typeof inputProps === 'function' ? inputProps({value, onChange}) : inputProps
                const defaultConfig = {label, error, options}

                const isolation = ['select', 'date', 'addressLookup', 'tel', 'phone'].includes(type)
                    ? 'auto'
                    : 'isolate'

                const isValid =
                    !!value &&
                    !error &&
                    (['tel', 'phone'].includes(type) ? validatePhoneNumber(value) : true)

                return (
                    <FormControl id={name} isInvalid={error}>
                        <InputGroup isolation={isolation}>
                            {type === 'textarea' && (
                                <TextAreaComponent
                                    ref={(node) => {
                                        ref(node)
                                        if (inputRef) inputRef.current = node
                                    }}
                                    isInvalid={!!error}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    value={value}
                                    label={label}
                                    placeholder={placeholder}
                                    autoComplete={autoComplete}
                                    rules={rules}
                                    {..._inputProps}
                                />
                            )}

                            {['text', 'password', 'email', 'number'].includes(type) && (
                                <InputComponent
                                    ref={(node) => {
                                        ref(node)
                                        if (inputRef) inputRef.current = node
                                    }}
                                    name={name}
                                    isInvalid={!!error}
                                    isDisabled={isDisabled}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    value={value}
                                    type={inputType}
                                    label={label}
                                    rules={rules}
                                    autoComplete={autoComplete}
                                    isSuccess={isValid && shouldDisplaySuccess}
                                    hideSuccessIcon={hideSuccessIcon}
                                    placeholder={placeholder}
                                    shouldRemoveLabel={shouldRemoveLabel}
                                    variant={variant}
                                    isProductRegistration={isProductRegistration}
                                    borderColor={borderColor}
                                    tooltip={tooltip}
                                    tooltipVariant={tooltipVariant}
                                    tooltipOpensOn={tooltipOpensOn}
                                    {..._inputProps}
                                />
                            )}

                            {['phone', 'tel'].includes(type) && (
                                <PhoneComponent
                                    ref={(node) => {
                                        ref(node)
                                        if (inputRef) inputRef.current = node
                                    }}
                                    name={name}
                                    isInvalid={
                                        !!error || (value ? !validatePhoneNumber(value) : false)
                                    }
                                    isDisabled={isDisabled}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    value={value}
                                    label={label}
                                    rules={rules}
                                    autoComplete={autoComplete}
                                    isSuccess={isValid && shouldDisplaySuccess}
                                    hideSuccessIcon={hideSuccessIcon}
                                    placeholder={placeholder}
                                    shouldRemoveLabel={shouldRemoveLabel}
                                    variant={variant}
                                    isProductRegistration={isProductRegistration}
                                    tooltip={tooltip}
                                    tooltipVariant={tooltipVariant}
                                    tooltipOpensOn={tooltipOpensOn}
                                    {..._inputProps}
                                />
                            )}

                            {type === 'date' && (
                                <DatePicker
                                    clearIcon={clearIcon}
                                    calendarIcon={calendarIcon}
                                    ref={ref}
                                    onChange={onChange}
                                    value={value}
                                    isEditForm={isEditForm}
                                    placeholder={placeholder}
                                    isDisabled={isDisabled}
                                    isInvalid
                                    name={name}
                                    label={label}
                                    variant={variant}
                                    maxDate={maxDate}
                                    isSuccess={isValid}
                                    {..._inputProps}
                                />
                            )}

                            {type === 'hidden' && (
                                <input
                                    ref={ref}
                                    onChange={onChange}
                                    value={value}
                                    type="hidden"
                                    {..._inputProps}
                                />
                            )}

                            {type === 'password' && (
                                <InputRightElement>
                                    <IconButton
                                        variant="ghosted"
                                        aria-label={passwordIconLabel}
                                        icon={<PasswordIcon color="gray.500" boxSize={6} />}
                                        onClick={() => setHidePassword(!hidePassword)}
                                    />
                                </InputRightElement>
                            )}

                            {type === 'select' && (
                                <Dropdown
                                    ref={ref}
                                    name={name}
                                    label={label}
                                    onChange={onChange}
                                    value={value}
                                    rules={rules}
                                    placeholder={placeholder}
                                    isSearchable={isSearchable}
                                    isDisabled={isDisabled}
                                    isInvalid={!!error}
                                    options={options}
                                    variant={variant}
                                    {..._inputProps}
                                />
                            )}

                            {type === 'select-html' && (
                                <Select
                                    ref={ref}
                                    onChange={onChange}
                                    value={value}
                                    placeholder={placeholder}
                                    {..._inputProps}
                                >
                                    {options.map((opt) => (
                                        <option key={`${opt.label}-${opt.value}`} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </Select>
                            )}

                            {type === 'checkbox' && (
                                <Checkbox
                                    ref={ref}
                                    onChange={(e) => onChange(e.target.checked)}
                                    isChecked={value}
                                    isDisabled={isDisabled}
                                    variant={variant}
                                    {..._inputProps}
                                >
                                    {formLabel || label}
                                </Checkbox>
                            )}

                            {type === 'addressLookup' && (
                                <LoqateComponent
                                    ref={ref}
                                    lookupAddressObject={getCustomInputValue(
                                        defaultValue,
                                        value,
                                        options,
                                    )}
                                    startConfig$={defaultConfig}
                                    isInvalid$={!!error}
                                    rules={rules}
                                    toggleAutoComplete={toggleAutoComplete}
                                    setAutoCompleteAvailable={setAutoCompleteAvailable}
                                    hideManualEntry={isContactUs}
                                    error={error}
                                    onBlur={onBlur}
                                    onChange={onChange}
                                    {...rest}
                                />
                            )}

                            {children}
                        </InputGroup>
                        {helpText && <Text {...styles.helperText}>{helpText}</Text>}
                        {error && type !== 'hidden' && (
                            <Text {...styles.errorText}>{error.message}</Text>
                        )}
                    </FormControl>
                )
            }}
        />
    )
}

Field.propTypes = {
    name: PropTypes.string,
    label: PropTypes.string,
    autoComplete: PropTypes.string,
    formLabel: PropTypes.any,
    type: PropTypes.oneOf([
        'text',
        'textarea',
        'number',
        'password',
        'email',
        'phone',
        'tel',
        'select',
        'checkbox',
        'hidden',
        'select-html',
        'addressLookup',
        'date',
    ]),
    options: PropTypes.arrayOf(PropTypes.shape({label: PropTypes.string, value: PropTypes.any})),
    rules: PropTypes.object,
    error: PropTypes.shape({message: PropTypes.string}),
    placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    inputProps: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    control: PropTypes.object,
    defaultValue: PropTypes.any,
    helpText: PropTypes.any,
    children: PropTypes.any,
    inputRef: PropTypes.object,
    isDisabled: PropTypes.bool,
    isSearchable: PropTypes.bool,
    variant: PropTypes.string,
    setAutoCompleteAvailable: PropTypes.func,
    toggleAutoComplete: PropTypes.func,
    shouldRemoveLabel: PropTypes.bool,
    isProductRegistration: PropTypes.bool,
    calendarIcon: PropTypes.oneOfType([PropTypes.bool, PropTypes.object, PropTypes.element]),
    clearIcon: PropTypes.bool,
    borderColor: PropTypes.string,
    shouldDisplaySuccess: PropTypes.bool,
    isEditForm: PropTypes.bool,
    tooltip: PropTypes.any,
    tooltipVariant: PropTypes.string,
    tooltipOpensOn: PropTypes.string,
    hideSuccessIcon: PropTypes.bool,
    maxDate: PropTypes.object,
    isContactUs: PropTypes.bool,
}

export default Field
